import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Menu,
  MenuButton,
  MenuItems,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Fragment, useState } from "react";

import { BlockchainIcon } from "~/components/logos/blockchain";
import type {
  Blockchain,
  BlockchainId,
  Deployment,
} from "~/lib/stablecoins.server";

type Props = Readonly<{
  imageURL: string;
  name: string;
  ticker: string;
  buyURL?: string;
  deployments?: Deployment[];
  tickerOnly?: boolean;
}>;

const EVM_CHAINS = new Set<BlockchainId>([
  "avalanche",
  "base",
  "celo",
  "ethereum",
  "optimism",
  "polygon",
]);

type AddChainError = {
  chain: Blockchain;
};

export function Token({
  buyURL,
  name,
  ticker,
  imageURL,
  deployments = [],
  tickerOnly = false,
}: Props): JSX.Element {
  const [isOpen, setOpen] = useState(false);
  const [addChainError, setAddChainError] = useState<AddChainError>();

  const hasEthWallet =
    typeof window !== "undefined" && typeof window.ethereum !== "undefined";

  const addToken = async (deployment: Deployment) => {
    try {
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [
          {
            chainId: deployment.chain.eth_chainId,
          },
        ],
      });

      try {
        await window.ethereum.request({
          method: "wallet_watchAsset",
          params: {
            type: "ERC20",
            options: {
              address: deployment.address,
              symbol: ticker,
              decimals: 18,
              image: window.location.origin + imageURL,
            },
          },
        });
      } catch {
        alert(`Unable to add ${ticker}`);
      }
    } catch {
      setAddChainError({ chain: deployment.chain });
      setOpen(true);
    }
  };

  return (
    <div className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between prose dark:prose-invert">
      <div className="flex items-center">
        <img
          src={imageURL}
          width={96}
          height={96}
          className="h-24 w-24 rounded-full border border-primary"
          alt={`${ticker} logo`}
        />
        <div className="ml-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-x-6">
            <h1 className="!text-3xl lg:!text-4xl xl:!text-5xl !leading-tight font-semibold m-0 whitespace-nowrap text-primary">
              {ticker}
            </h1>
            {!tickerOnly && (
              <h2 className="mt-1 lg:mt-0 mb-0 text-secondary font-normal !text-base lg:!text-4xl xl:!text-5xl">
                {name}
              </h2>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-x-4">
        {buyURL && (
          <a
            href={buyURL}
            className="btn primary w-full justify-center no-underline mt-6 sm:mt-0"
          >
            Apply to buy
          </a>
        )}
        {buyURL &&
          buyURL !== "https://app.brale.xyz/buy/signup/MXNe" &&
          buyURL !== "https://app.brale.xyz/buy/signup/USD*" &&
          hasEthWallet && (
            <>
              <Menu
                as="div"
                className="relative sm:inline-block text-left w-full"
              >
                <div>
                  <MenuButton className="btn nav secondary w-full justify-center mt-6 sm:mt-0">
                    Add to wallet
                  </MenuButton>
                </div>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <MenuItems className="absolute right-0 z-10 mt-4 w-56 origin-top-right round bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-900 dark:ring-gray-800 dark:text-white border border-primary overflow-clip">
                    <div>
                      {deployments
                        .filter((d) => EVM_CHAINS.has(d.chain.id))
                        .map((d) => (
                          <Menu.Item key={d.chain.id}>
                            <button
                              onClick={() => addToken(d)}
                              className={
                                "px-4 py-2 text-sm flex items-center w-full hover:bg-gray-100 dark:hover:bg-black"
                              }
                            >
                              <span>{d.chain.name}</span>
                              <div className="ml-auto">
                                <BlockchainIcon
                                  chainId={d.chain.id}
                                  key={d.chain.id}
                                />
                              </div>
                            </button>
                          </Menu.Item>
                        ))}
                    </div>
                  </MenuItems>
                </Transition>
              </Menu>
              <Transition show={isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-[210]" onClose={setOpen}>
                  <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="fixed inset-0 bg-black bg-opacity-70 transition-opacity" />
                  </TransitionChild>

                  <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                      <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                      >
                        <DialogPanel className="relative transform overflow-hidden round bg-white dark:bg-gray-900 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 border border-primary">
                          <div className="sm:flex sm:items-start">
                            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                              {addChainError && (
                                <BlockchainIcon
                                  chainId={addChainError.chain.id}
                                />
                              )}
                            </div>
                            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                              <DialogTitle
                                as="h3"
                                className="text-xl font-medium leading-6 text-black dark:text-white"
                              >
                                Unable to find chain
                              </DialogTitle>
                              <div className="mt-2">
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  It looks like you still need to add{" "}
                                  {addChainError?.chain.name} to your wallet.
                                  Head over to{" "}
                                  <a
                                    className="link"
                                    target="_blank"
                                    rel="noreferrer"
                                    href={`https://chainlist.org/chain/${Number(
                                      addChainError?.chain.eth_chainId
                                    )}`}
                                  >
                                    Chainlist
                                  </a>
                                  , add {addChainError?.chain.name}, then come
                                  back and try again.
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                            <button
                              type="button"
                              className="mt-3 inline-flex w-full justify-center rounded-full bg-white px-3 py-2  sm:mt-0 sm:w-auto btn primary"
                              onClick={() => setOpen(false)}
                            >
                              OK
                            </button>
                          </div>
                        </DialogPanel>
                      </TransitionChild>
                    </div>
                  </div>
                </Dialog>
              </Transition>
            </>
          )}
      </div>
    </div>
  );
}
